import { appPageBoilerplateFetch } from '../functions/pageBoilerplate.js'
import { pageBoilerplateFetch } from '../functions/tickets/ticketUtils.js'

const ENVS = ['prod', 'staging', 'testing', 'dev', 'upcoming', 'next']

const STATUS = {
  pass: { color: 'green', label: 'Working' },
  fail: { color: 'red', label: 'Failure' },
  unchecked: { color: 'gray', label: 'Not checked' }
}

const CHECK_TYPES = {
  crud: { subs: ['C', 'R', 'U', 'D'] },
  ditd: { columns: ['Designed', 'Implemented', 'Tested', 'Documented'] },
  sdk: { columns: ['Implemented', 'Test-Auto', 'Passing', 'Documented'] }
}

// ── QA categories ──
const QA_GROUPS = [
  {
    key: 'manual-features',
    title: 'Manual QA — Features',
    subtitle: 'UI features tested manually per environment',
  },
  {
    key: 'manual-sidebar',
    title: 'Manual QA — Sidebar CRUD',
    subtitle: 'Sidebar panel operations: Create, Read, Update, Delete',
  },
  {
    key: 'auto-packages',
    title: 'Automatic QA — Packages',
    subtitle: 'Package health checked via CI / tests',
  },
  {
    key: 'auto-sdk',
    title: 'Automatic QA — SDK Functions',
    subtitle: 'Implemented, Test-Automated, Passing, Documented',
  },
  {
    key: 'auto-props',
    title: 'Automatic QA — No-code Props',
    subtitle: 'Designed, Implemented, Tested, Documented',
  }
]

// ── Features data ──
const FEATURES = [
  // ═══ manual-features ═══
  // Account
  { name: 'SSO - Google', epic: 'Account', priority: 'P0', qa: 'manual-features' },
  { name: 'Show my projects', epic: 'Account', priority: 'P0', qa: 'manual-features' },
  { name: 'Switch projects', epic: 'Account', priority: 'P0', qa: 'manual-features' },
  { name: 'User profile', epic: 'Account', priority: 'P0', qa: 'manual-features' },
  { name: 'Sign out', epic: 'Account', priority: 'P0', qa: 'manual-features' },
  { name: 'Settings', epic: 'Account', priority: 'P0', qa: 'manual-features' },
  { name: 'Plans & payment', epic: 'Account', priority: 'P0', qa: 'manual-features' },
  { name: 'SSO - Github', epic: 'Account', priority: 'P1', qa: 'manual-features' },
  { name: 'Removing Project', epic: 'Account', priority: 'P1', qa: 'manual-features' },
  { name: 'Permissions & IAM', epic: 'Account', priority: 'P1', qa: 'manual-features' },
  { name: 'Teams & invite', epic: 'Account', priority: 'P1', qa: 'manual-features' },
  { name: 'Access control', epic: 'Account', priority: 'P1', qa: 'manual-features' },
  { name: 'Email signing', epic: 'Account', priority: 'P2', qa: 'manual-features' },
  { name: 'Reset password & email', epic: 'Account', priority: 'P2', qa: 'manual-features' },
  // Landing
  { name: 'Landing + Responsive', epic: 'Landing', priority: 'P0', qa: 'manual-features' },
  { name: 'Features & benefits content', epic: 'Landing', priority: 'P2', qa: 'manual-features' },
  // Canvas
  { name: 'Component Editor', epic: 'Canvas', priority: 'P0', qa: 'manual-features' },
  { name: 'Component viewer', epic: 'Canvas', priority: 'P0', qa: 'manual-features' },
  { name: 'Builtin components', epic: 'Canvas', priority: 'P0', qa: 'manual-features' },
  { name: 'Components', epic: 'Canvas', priority: 'P0', qa: 'manual-features' },
  { name: 'Functions', epic: 'Canvas', priority: 'P0', qa: 'manual-features' },
  { name: 'Dependencies', epic: 'Canvas', priority: 'P0', qa: 'manual-features' },
  { name: 'Download panel', epic: 'Canvas', priority: 'P0', qa: 'manual-features' },
  { name: 'Components Library', epic: 'Canvas', priority: 'P0', qa: 'manual-features' },
  { name: 'Context menu', epic: 'Canvas', priority: 'P0', qa: 'manual-features' },
  { name: 'Share panel', epic: 'Canvas', priority: 'P0', qa: 'manual-features' },
  { name: 'URL to canvas', epic: 'Canvas', priority: 'P0', qa: 'manual-features' },
  { name: 'Copy/Paste', epic: 'Canvas', priority: 'P1', qa: 'manual-features' },
  { name: 'Libraries', epic: 'Canvas', priority: 'P1', qa: 'manual-features' },
  { name: 'Preview panel', epic: 'Canvas', priority: 'P1', qa: 'manual-features' },
  { name: 'Layers', epic: 'Canvas', priority: 'P1', qa: 'manual-features' },
  { name: 'Files', epic: 'Canvas', priority: 'P1', qa: 'manual-features' },
  { name: 'Layers panel', epic: 'Canvas', priority: 'P1', qa: 'manual-features' },
  { name: 'Layers and canvas interaction', epic: 'Canvas', priority: 'P1', qa: 'manual-features' },
  { name: 'Context menu on Canvas', epic: 'Canvas', priority: 'P1', qa: 'manual-features' },
  { name: 'Rename canvas page', epic: 'Canvas', priority: 'P1', qa: 'manual-features' },
  { name: 'Real-time cursor', epic: 'Canvas', priority: 'P2', qa: 'manual-features' },
  { name: 'Real-time changes', epic: 'Canvas', priority: 'P2', qa: 'manual-features' },
  { name: 'Methods', epic: 'Canvas', priority: 'P2', qa: 'manual-features' },
  { name: 'Snippets', epic: 'Canvas', priority: 'P2', qa: 'manual-features' },
  { name: 'Secrets', epic: 'Canvas', priority: 'P2', qa: 'manual-features' },
  { name: 'Shared library switching', epic: 'Canvas', priority: 'P2', qa: 'manual-features' },
  { name: 'Keyboard shortcuts', epic: 'Canvas', priority: 'P2', qa: 'manual-features' },
  { name: 'Canvas Pages', epic: 'Canvas', priority: 'P2', qa: 'manual-features' },
  { name: 'SEO on pages', epic: 'Canvas', priority: 'P2', qa: 'manual-features' },
  // Scratch
  { name: 'Scratch', epic: 'Canvas', priority: 'P0', qa: 'manual-features' },
  { name: 'Scratch: colours', epic: 'Canvas', priority: 'P0', qa: 'manual-features' },
  { name: 'Scratch: typography', epic: 'Canvas', priority: 'P0', qa: 'manual-features' },
  { name: 'Scratch: spacing', epic: 'Canvas', priority: 'P0', qa: 'manual-features' },
  { name: 'Scratch: icons', epic: 'Canvas', priority: 'P0', qa: 'manual-features' },
  { name: 'Scratch: fonts', epic: 'Canvas', priority: 'P0', qa: 'manual-features' },
  { name: 'Scratch: responsive', epic: 'Canvas', priority: 'P0', qa: 'manual-features' },
  { name: 'Scratch: timing', epic: 'Canvas', priority: 'P0', qa: 'manual-features' },
  { name: 'Scratch: cases', epic: 'Canvas', priority: 'P0', qa: 'manual-features' },
  { name: 'Scratch: shapes', epic: 'Canvas', priority: 'P2', qa: 'manual-features' },
  { name: 'Scratch: grids', epic: 'Canvas', priority: 'P2', qa: 'manual-features' },
  // DOMQL
  { name: 'DOMQL', epic: 'DOMQL', priority: 'P0', qa: 'manual-features' },
  { name: 'css-props', epic: 'DOMQL', priority: 'P0', qa: 'manual-features' },
  { name: 'Attr-in-props', epic: 'DOMQL', priority: 'P0', qa: 'manual-features' },
  { name: 'State', epic: 'DOMQL', priority: 'P0', qa: 'manual-features' },
  { name: 'Props', epic: 'DOMQL', priority: 'P0', qa: 'manual-features' },
  { name: 'If', epic: 'DOMQL', priority: 'P0', qa: 'manual-features' },
  { name: 'Content', epic: 'DOMQL', priority: 'P0', qa: 'manual-features' },
  { name: 'Children', epic: 'DOMQL', priority: 'P0', qa: 'manual-features' },
  { name: 'Extends', epic: 'DOMQL', priority: 'P0', qa: 'manual-features' },
  { name: 'Child Extends', epic: 'DOMQL', priority: 'P0', qa: 'manual-features' },
  { name: 'Child Props', epic: 'DOMQL', priority: 'P0', qa: 'manual-features' },
  { name: 'DOMQL Methods', epic: 'DOMQL', priority: 'P0', qa: 'manual-features' },
  { name: 'Router', epic: 'DOMQL', priority: 'P0', qa: 'manual-features' },
  // SDK
  { name: 'SDK', epic: 'SDK', priority: 'P0', qa: 'manual-features' },
  // Platform
  { name: 'Versioning', epic: 'Platform', priority: 'P0', qa: 'manual-features' },
  { name: 'React preview', epic: 'Platform', priority: 'P1', qa: 'manual-features' },
  { name: 'Realtime collaboration', epic: 'Platform', priority: 'P1', qa: 'manual-features' },
  { name: 'Branching', epic: 'Platform', priority: 'P2', qa: 'manual-features' },
  { name: 'Pull requests', epic: 'Platform', priority: 'P2', qa: 'manual-features' },
  // Design System
  { name: 'Colors', epic: 'Design System', priority: 'P0', qa: 'manual-features' },
  { name: 'Global themes', epic: 'Design System', priority: 'P0', qa: 'manual-features' },
  { name: 'Themes', epic: 'Design System', priority: 'P0', qa: 'manual-features' },
  { name: 'Typography', epic: 'Design System', priority: 'P0', qa: 'manual-features' },
  { name: 'Spacing', epic: 'Design System', priority: 'P0', qa: 'manual-features' },
  { name: 'Semantic Colors', epic: 'Design System', priority: 'P0', qa: 'manual-features' },
  { name: 'Cases', epic: 'Design System', priority: 'P1', qa: 'manual-features' },
  { name: 'Timing', epic: 'Design System', priority: 'P2', qa: 'manual-features' },
  // Integrations
  { name: 'Server preview', epic: 'Integrations', priority: 'P0', qa: 'manual-features' },
  { name: 'VS Code', epic: 'Integrations', priority: 'P0', qa: 'manual-features' },
  { name: 'Chrome extension', epic: 'Integrations', priority: 'P1', qa: 'manual-features' },
  { name: 'Custom domain', epic: 'Integrations', priority: 'P1', qa: 'manual-features' },
  { name: 'Download: Screenshot', epic: 'Integrations', priority: 'P1', qa: 'manual-features' },
  { name: 'Download: SVG', epic: 'Integrations', priority: 'P2', qa: 'manual-features' },
  { name: 'Download: HTML', epic: 'Integrations', priority: 'P2', qa: 'manual-features' },
  { name: 'Download: PDF', epic: 'Integrations', priority: 'P2', qa: 'manual-features' },
  { name: 'Download: CDN', epic: 'Integrations', priority: 'P2', qa: 'manual-features' },
  // smbls (manual)
  { name: 'Symbols fetch', epic: 'smbls', priority: 'P1', qa: 'manual-features' },
  { name: 'Symbols login', epic: 'smbls', priority: 'P1', qa: 'manual-features' },
  { name: 'Symbols push', epic: 'smbls', priority: 'P1', qa: 'manual-features' },
  { name: 'Symbols create', epic: 'smbls', priority: 'P0', qa: 'manual-features' },
  // Onboarding
  { name: 'Show 3x Templates', epic: 'Onboarding', priority: 'P0', qa: 'manual-features' },
  { name: 'Onboarding tutorials', epic: 'Onboarding', priority: 'P1', qa: 'manual-features' },
  { name: 'General tutorials', epic: 'Onboarding', priority: 'P1', qa: 'manual-features' },

  // ═══ manual-sidebar (CRUD) ═══
  { name: 'Selected Canvas', epic: 'Sidebar', priority: 'P0', qa: 'manual-sidebar', check: 'crud' },
  { name: 'Components', epic: 'Sidebar', priority: 'P0', qa: 'manual-sidebar', check: 'crud' },
  { name: 'Pages', epic: 'Sidebar', priority: 'P0', qa: 'manual-sidebar', check: 'crud' },
  { name: 'Functions', epic: 'Sidebar', priority: 'P0', qa: 'manual-sidebar', check: 'crud' },
  { name: 'Files', epic: 'Sidebar', priority: 'P0', qa: 'manual-sidebar', check: 'crud' },
  { name: 'Color', epic: 'Sidebar', priority: 'P0', qa: 'manual-sidebar', check: 'crud' },
  { name: 'Gradient', epic: 'Sidebar', priority: 'P0', qa: 'manual-sidebar', check: 'crud' },
  { name: 'Semantic Color', epic: 'Sidebar', priority: 'P0', qa: 'manual-sidebar', check: 'crud' },
  { name: 'Global Theme', epic: 'Sidebar', priority: 'P0', qa: 'manual-sidebar', check: 'crud' },
  { name: 'Themes', epic: 'Sidebar', priority: 'P0', qa: 'manual-sidebar', check: 'crud' },
  { name: 'Typography', epic: 'Sidebar', priority: 'P0', qa: 'manual-sidebar', check: 'crud' },
  { name: 'Font Families', epic: 'Sidebar', priority: 'P0', qa: 'manual-sidebar', check: 'crud' },
  { name: 'Text Styles', epic: 'Sidebar', priority: 'P0', qa: 'manual-sidebar', check: 'crud' },
  { name: 'Font', epic: 'Sidebar', priority: 'P0', qa: 'manual-sidebar', check: 'crud' },
  { name: 'Spacing', epic: 'Sidebar', priority: 'P0', qa: 'manual-sidebar', check: 'crud' },
  { name: 'Icons', epic: 'Sidebar', priority: 'P0', qa: 'manual-sidebar', check: 'crud' },
  { name: 'Cases', epic: 'Sidebar', priority: 'P0', qa: 'manual-sidebar', check: 'crud' },
  { name: 'Media', epic: 'Sidebar', priority: 'P0', qa: 'manual-sidebar', check: 'crud' },
  { name: 'Dependencies', epic: 'Sidebar', priority: 'P0', qa: 'manual-sidebar', check: 'crud' },
  { name: 'Branches', epic: 'Sidebar', priority: 'P1', qa: 'manual-sidebar', check: 'crud' },
  { name: 'Shared Libraries', epic: 'Sidebar', priority: 'P1', qa: 'manual-sidebar', check: 'crud' },

  // ═══ auto-packages ═══
  // Server
  { name: 'agent-runner', epic: 'Server', priority: 'P1', qa: 'auto-packages' },
  { name: 'ai-providers', epic: 'Server', priority: 'P1', qa: 'auto-packages' },
  { name: 'analyzed', epic: 'Server', priority: 'P1', qa: 'auto-packages' },
  { name: 'centauric', epic: 'Server', priority: 'P1', qa: 'auto-packages' },
  { name: 'cloudflare-dns', epic: 'Server', priority: 'P1', qa: 'auto-packages' },
  { name: 'config', epic: 'Server', priority: 'P1', qa: 'auto-packages' },
  { name: 'custom-domains', epic: 'Server', priority: 'P1', qa: 'auto-packages' },
  { name: 'gateway', epic: 'Server', priority: 'P0', qa: 'auto-packages' },
  { name: 'get-cdn-file', epic: 'Server', priority: 'P1', qa: 'auto-packages' },
  { name: 'kv', epic: 'Server', priority: 'P1', qa: 'auto-packages' },
  { name: 'logger', epic: 'Server', priority: 'P1', qa: 'auto-packages' },
  { name: 'mermaid-server', epic: 'Server', priority: 'P1', qa: 'auto-packages' },
  { name: 'puppeteer', epic: 'Server', priority: 'P1', qa: 'auto-packages' },
  { name: 'screenshot', epic: 'Server', priority: 'P1', qa: 'auto-packages' },
  { name: 'socket', epic: 'Server', priority: 'P0', qa: 'auto-packages' },
  { name: 'storage', epic: 'Server', priority: 'P1', qa: 'auto-packages' },
  { name: 'symbols-mcp', epic: 'Server', priority: 'P0', qa: 'auto-packages' },
  { name: 'symstory', epic: 'Server', priority: 'P1', qa: 'auto-packages' },
  { name: 'tauri', epic: 'Server', priority: 'P1', qa: 'auto-packages' },
  { name: 'versioning', epic: 'Server', priority: 'P1', qa: 'auto-packages' },
  // Symbolize
  { name: 'symbolize-cli', epic: 'Symbolize', priority: 'P1', qa: 'auto-packages' },
  { name: 'symbolize-core', epic: 'Symbolize', priority: 'P0', qa: 'auto-packages' },
  { name: 'symbolize-dompiler', epic: 'Symbolize', priority: 'P1', qa: 'auto-packages' },
  { name: 'symbolize-editorjs', epic: 'Symbolize', priority: 'P1', qa: 'auto-packages' },
  { name: 'symbolize-html', epic: 'Symbolize', priority: 'P1', qa: 'auto-packages' },
  { name: 'symbolize-markdown', epic: 'Symbolize', priority: 'P1', qa: 'auto-packages' },
  { name: 'symbolize-mcp', epic: 'Symbolize', priority: 'P1', qa: 'auto-packages' },
  { name: 'symbolize-react', epic: 'Symbolize', priority: 'P1', qa: 'auto-packages' },
  { name: 'symbolize-tailwind', epic: 'Symbolize', priority: 'P1', qa: 'auto-packages' },
  // smbls packages
  { name: 'attrs-in-props', epic: 'smbls', priority: 'P0', qa: 'auto-packages' },
  { name: 'cli', epic: 'smbls', priority: 'P0', qa: 'auto-packages' },
  { name: 'create', epic: 'smbls', priority: 'P0', qa: 'auto-packages' },
  { name: 'css', epic: 'smbls', priority: 'P0', qa: 'auto-packages' },
  { name: 'css-in-props', epic: 'smbls', priority: 'P0', qa: 'auto-packages' },
  { name: 'default-config', epic: 'smbls', priority: 'P1', qa: 'auto-packages' },
  { name: 'domql', epic: 'smbls', priority: 'P0', qa: 'auto-packages' },
  { name: 'element', epic: 'smbls', priority: 'P0', qa: 'auto-packages' },
  { name: 'fetch', epic: 'smbls', priority: 'P1', qa: 'auto-packages' },
  { name: 'icons', epic: 'smbls', priority: 'P1', qa: 'auto-packages' },
  { name: 'scratch', epic: 'smbls', priority: 'P0', qa: 'auto-packages' },
  { name: 'signal', epic: 'smbls', priority: 'P0', qa: 'auto-packages' },
  { name: 'smbls', epic: 'smbls', priority: 'P0', qa: 'auto-packages' },
  { name: 'smbls-utils', epic: 'smbls', priority: 'P1', qa: 'auto-packages' },
  { name: 'state', epic: 'smbls', priority: 'P0', qa: 'auto-packages' },
  { name: 'sync', epic: 'smbls', priority: 'P1', qa: 'auto-packages' },
  { name: 'utils', epic: 'smbls', priority: 'P1', qa: 'auto-packages' },
  { name: 'wasm', epic: 'smbls', priority: 'P1', qa: 'auto-packages' },
  // Plugins
  { name: 'brender', epic: 'Plugins', priority: 'P1', qa: 'auto-packages' },
  { name: 'capsize', epic: 'Plugins', priority: 'P1', qa: 'auto-packages' },
  { name: 'editorjs', epic: 'Plugins', priority: 'P1', qa: 'auto-packages' },
  { name: 'plugin-fetch', epic: 'Plugins', priority: 'P1', qa: 'auto-packages' },
  { name: 'frank', epic: 'Plugins', priority: 'P0', qa: 'auto-packages' },
  { name: 'freestyler', epic: 'Plugins', priority: 'P1', qa: 'auto-packages' },
  { name: 'funcql', epic: 'Plugins', priority: 'P1', qa: 'auto-packages' },
  { name: 'grabber-kid', epic: 'Plugins', priority: 'P1', qa: 'auto-packages' },
  { name: 'helmet', epic: 'Plugins', priority: 'P1', qa: 'auto-packages' },
  { name: 'keyflows', epic: 'Plugins', priority: 'P1', qa: 'auto-packages' },
  { name: 'mermaid', epic: 'Plugins', priority: 'P1', qa: 'auto-packages' },
  { name: 'parse', epic: 'Plugins', priority: 'P1', qa: 'auto-packages' },
  { name: 'performance', epic: 'Plugins', priority: 'P1', qa: 'auto-packages' },
  { name: 'polyglot', epic: 'Plugins', priority: 'P1', qa: 'auto-packages' },
  { name: 'qsql', epic: 'Plugins', priority: 'P1', qa: 'auto-packages' },
  { name: 'report', epic: 'Plugins', priority: 'P1', qa: 'auto-packages' },
  { name: 'router', epic: 'Plugins', priority: 'P0', qa: 'auto-packages' },
  { name: 'shorthand', epic: 'Plugins', priority: 'P1', qa: 'auto-packages' },
  { name: 'plugin-sync', epic: 'Plugins', priority: 'P1', qa: 'auto-packages' },
  // Editor
  { name: 'preview', epic: 'Editor', priority: 'P0', qa: 'auto-packages' },
  { name: 'convert', epic: 'Editor', priority: 'P0', qa: 'auto-packages' },
  { name: 'canvas', epic: 'Editor', priority: 'P0', qa: 'auto-packages' },
  { name: 'assistant', epic: 'Editor', priority: 'P1', qa: 'auto-packages' },
  { name: 'docs', epic: 'Editor', priority: 'P1', qa: 'auto-packages' },
  // Commercials / Docs / Dompiler / MCP
  { name: 'landing', epic: 'Commercials', priority: 'P0', qa: 'auto-packages' },
  { name: 'developers', epic: 'Docs', priority: 'P1', qa: 'auto-packages' },
  { name: 'dompiler', epic: 'Dompiler', priority: 'P0', qa: 'auto-packages' },
  { name: 'mcp-local', epic: 'MCP', priority: 'P0', qa: 'auto-packages' },
  { name: 'mcp-https', epic: 'MCP', priority: 'P0', qa: 'auto-packages' },

  // ═══ auto-sdk (SDK functions) ═══
  // P0
  { name: 'acceptInvite', epic: 'SDK', priority: 'P0', qa: 'auto-sdk', check: 'sdk' },
  { name: 'addItem', epic: 'SDK', priority: 'P0', qa: 'auto-sdk', check: 'sdk' },
  { name: 'addMultipleItems', epic: 'SDK', priority: 'P0', qa: 'auto-sdk', check: 'sdk' },
  { name: 'auth', epic: 'SDK', priority: 'P0', qa: 'auto-sdk', check: 'sdk' },
  { name: 'confirmPasswordChange', epic: 'SDK', priority: 'P0', qa: 'auto-sdk', check: 'sdk' },
  { name: 'confirmPasswordReset', epic: 'SDK', priority: 'P0', qa: 'auto-sdk', check: 'sdk' },
  { name: 'confirmRegistration', epic: 'SDK', priority: 'P0', qa: 'auto-sdk', check: 'sdk' },
  { name: 'connect', epic: 'SDK', priority: 'P0', qa: 'auto-sdk', check: 'sdk' },
  { name: 'createProject', epic: 'SDK', priority: 'P0', qa: 'auto-sdk', check: 'sdk' },
  { name: 'createStorageBucket', epic: 'SDK', priority: 'P0', qa: 'auto-sdk', check: 'sdk' },
  { name: 'deleteItem', epic: 'SDK', priority: 'P0', qa: 'auto-sdk', check: 'sdk' },
  { name: 'duplicateProject', epic: 'SDK', priority: 'P0', qa: 'auto-sdk', check: 'sdk' },
  { name: 'fetchUser', epic: 'SDK', priority: 'P0', qa: 'auto-sdk', check: 'sdk' },
  { name: 'getChanges', epic: 'SDK', priority: 'P0', qa: 'auto-sdk', check: 'sdk' },
  { name: 'getData', epic: 'SDK', priority: 'P0', qa: 'auto-sdk', check: 'sdk' },
  { name: 'getItem', epic: 'SDK', priority: 'P0', qa: 'auto-sdk', check: 'sdk' },
  { name: 'getProject', epic: 'SDK', priority: 'P0', qa: 'auto-sdk', check: 'sdk' },
  { name: 'getProjectByKey', epic: 'SDK', priority: 'P0', qa: 'auto-sdk', check: 'sdk' },
  { name: 'getProjectLibraries', epic: 'SDK', priority: 'P0', qa: 'auto-sdk', check: 'sdk' },
  { name: 'getProjectMembers', epic: 'SDK', priority: 'P0', qa: 'auto-sdk', check: 'sdk' },
  { name: 'getStorageBucket', epic: 'SDK', priority: 'P0', qa: 'auto-sdk', check: 'sdk' },
  { name: 'getStoredAuthState', epic: 'SDK', priority: 'P0', qa: 'auto-sdk', check: 'sdk' },
  { name: 'getUser', epic: 'SDK', priority: 'P0', qa: 'auto-sdk', check: 'sdk' },
  { name: 'getUserByEmail', epic: 'SDK', priority: 'P0', qa: 'auto-sdk', check: 'sdk' },
  { name: 'githubAuth', epic: 'SDK', priority: 'P0', qa: 'auto-sdk', check: 'sdk' },
  { name: 'googleAuth', epic: 'SDK', priority: 'P0', qa: 'auto-sdk', check: 'sdk' },
  { name: 'inviteMember', epic: 'SDK', priority: 'P0', qa: 'auto-sdk', check: 'sdk' },
  { name: 'login', epic: 'SDK', priority: 'P0', qa: 'auto-sdk', check: 'sdk' },
  { name: 'logout', epic: 'SDK', priority: 'P0', qa: 'auto-sdk', check: 'sdk' },
  { name: 'publish', epic: 'SDK', priority: 'P0', qa: 'auto-sdk', check: 'sdk' },
  { name: 'redo', epic: 'SDK', priority: 'P0', qa: 'auto-sdk', check: 'sdk' },
  { name: 'register', epic: 'SDK', priority: 'P0', qa: 'auto-sdk', check: 'sdk' },
  { name: 'removeMember', epic: 'SDK', priority: 'P0', qa: 'auto-sdk', check: 'sdk' },
  { name: 'removeProject', epic: 'SDK', priority: 'P0', qa: 'auto-sdk', check: 'sdk' },
  { name: 'removeStorageBucket', epic: 'SDK', priority: 'P0', qa: 'auto-sdk', check: 'sdk' },
  { name: 'requestPasswordChange', epic: 'SDK', priority: 'P0', qa: 'auto-sdk', check: 'sdk' },
  { name: 'requestPasswordReset', epic: 'SDK', priority: 'P0', qa: 'auto-sdk', check: 'sdk' },
  { name: 'restoreVersion', epic: 'SDK', priority: 'P0', qa: 'auto-sdk', check: 'sdk' },
  { name: 'setBucket', epic: 'SDK', priority: 'P0', qa: 'auto-sdk', check: 'sdk' },
  { name: 'setProject', epic: 'SDK', priority: 'P0', qa: 'auto-sdk', check: 'sdk' },
  { name: 'setUser', epic: 'SDK', priority: 'P0', qa: 'auto-sdk', check: 'sdk' },
  { name: 'switchVersion', epic: 'SDK', priority: 'P0', qa: 'auto-sdk', check: 'sdk' },
  { name: 'undo', epic: 'SDK', priority: 'P0', qa: 'auto-sdk', check: 'sdk' },
  { name: 'updateData', epic: 'SDK', priority: 'P0', qa: 'auto-sdk', check: 'sdk' },
  { name: 'updateItem', epic: 'SDK', priority: 'P0', qa: 'auto-sdk', check: 'sdk' },
  { name: 'updateProject', epic: 'SDK', priority: 'P0', qa: 'auto-sdk', check: 'sdk' },
  { name: 'updateProjectIcon', epic: 'SDK', priority: 'P0', qa: 'auto-sdk', check: 'sdk' },
  { name: 'updateProjectName', epic: 'SDK', priority: 'P0', qa: 'auto-sdk', check: 'sdk' },
  { name: 'updateUserProfile', epic: 'SDK', priority: 'P0', qa: 'auto-sdk', check: 'sdk' },
  { name: 'updateUserRole', epic: 'SDK', priority: 'P0', qa: 'auto-sdk', check: 'sdk' },
  { name: 'uploadFile', epic: 'SDK', priority: 'P0', qa: 'auto-sdk', check: 'sdk' },
  // P1
  { name: 'acceptOwnerInvitation', epic: 'SDK', priority: 'P1', qa: 'auto-sdk', check: 'sdk' },
  { name: 'addProjectLibraries', epic: 'SDK', priority: 'P1', qa: 'auto-sdk', check: 'sdk' },
  { name: 'call', epic: 'SDK', priority: 'P1', qa: 'auto-sdk', check: 'sdk' },
  { name: 'cancelSubscription', epic: 'SDK', priority: 'P1', qa: 'auto-sdk', check: 'sdk' },
  { name: 'checkout', epic: 'SDK', priority: 'P1', qa: 'auto-sdk', check: 'sdk' },
  { name: 'checkSubscriptionStatus', epic: 'SDK', priority: 'P1', qa: 'auto-sdk', check: 'sdk' },
  { name: 'createBranch', epic: 'SDK', priority: 'P1', qa: 'auto-sdk', check: 'sdk' },
  { name: 'createDnsRecord', epic: 'SDK', priority: 'P1', qa: 'auto-sdk', check: 'sdk' },
  { name: 'deleteBranch', epic: 'SDK', priority: 'P1', qa: 'auto-sdk', check: 'sdk' },
  { name: 'downgradeSubscription', epic: 'SDK', priority: 'P1', qa: 'auto-sdk', check: 'sdk' },
  { name: 'editBranch', epic: 'SDK', priority: 'P1', qa: 'auto-sdk', check: 'sdk' },
  { name: 'getAvailableLibraries', epic: 'SDK', priority: 'P1', qa: 'auto-sdk', check: 'sdk' },
  { name: 'getBranches', epic: 'SDK', priority: 'P1', qa: 'auto-sdk', check: 'sdk' },
  { name: 'hasGlobalPermission', epic: 'SDK', priority: 'P1', qa: 'auto-sdk', check: 'sdk' },
  { name: 'hasPermission', epic: 'SDK', priority: 'P1', qa: 'auto-sdk', check: 'sdk' },
  { name: 'inviteAccountOwner', epic: 'SDK', priority: 'P1', qa: 'auto-sdk', check: 'sdk' },
  { name: 'mergeBranch', epic: 'SDK', priority: 'P1', qa: 'auto-sdk', check: 'sdk' },
  { name: 'removeProjectLibraries', epic: 'SDK', priority: 'P1', qa: 'auto-sdk', check: 'sdk' },
  { name: 'send', epic: 'SDK', priority: 'P1', qa: 'auto-sdk', check: 'sdk' },
  { name: 'set', epic: 'SDK', priority: 'P1', qa: 'auto-sdk', check: 'sdk' },
  { name: 'switchBranch', epic: 'SDK', priority: 'P1', qa: 'auto-sdk', check: 'sdk' },
  // P2
  { name: 'removeDnsRecord', epic: 'SDK', priority: 'P2', qa: 'auto-sdk', check: 'sdk' },
  { name: 'getDnsRecord', epic: 'SDK', priority: 'P2', qa: 'auto-sdk', check: 'sdk' },
  { name: 'listFeatureFlags', epic: 'SDK', priority: 'P2', qa: 'auto-sdk', check: 'sdk' },
  { name: 'prompt', epic: 'SDK', priority: 'P2', qa: 'auto-sdk', check: 'sdk' },
  { name: 'removeFeatureFlag', epic: 'SDK', priority: 'P2', qa: 'auto-sdk', check: 'sdk' },
  { name: 'setProjectDomains', epic: 'SDK', priority: 'P2', qa: 'auto-sdk', check: 'sdk' },
  // Unprioritized
  { name: 'batchUpdateFeatureFlags', epic: 'SDK', priority: 'P2', qa: 'auto-sdk', check: 'sdk' },
  { name: 'checkFeatureAccess', epic: 'SDK', priority: 'P2', qa: 'auto-sdk', check: 'sdk' },
  { name: 'checkProjectKeyAvailability', epic: 'SDK', priority: 'P2', qa: 'auto-sdk', check: 'sdk' },
  { name: 'checkResourceLimit', epic: 'SDK', priority: 'P2', qa: 'auto-sdk', check: 'sdk' },
  { name: 'generateInvoice', epic: 'SDK', priority: 'P2', qa: 'auto-sdk', check: 'sdk' },
  { name: 'getAvailableFeatures', epic: 'SDK', priority: 'P2', qa: 'auto-sdk', check: 'sdk' },
  { name: 'getSubscriptionDetails', epic: 'SDK', priority: 'P2', qa: 'auto-sdk', check: 'sdk' },
  { name: 'getUsageReport', epic: 'SDK', priority: 'P2', qa: 'auto-sdk', check: 'sdk' },
  { name: 'getUserFeatures', epic: 'SDK', priority: 'P2', qa: 'auto-sdk', check: 'sdk' },
  { name: 'listPlans', epic: 'SDK', priority: 'P2', qa: 'auto-sdk', check: 'sdk' },
  { name: 'query', epic: 'SDK', priority: 'P2', qa: 'auto-sdk', check: 'sdk' },
  { name: 'reactivateSubscription', epic: 'SDK', priority: 'P2', qa: 'auto-sdk', check: 'sdk' },
  { name: 'reconnect', epic: 'SDK', priority: 'P2', qa: 'auto-sdk', check: 'sdk' },
  { name: 'removeAccountOwner', epic: 'SDK', priority: 'P2', qa: 'auto-sdk', check: 'sdk' },
  { name: 'subscribe', epic: 'SDK', priority: 'P2', qa: 'auto-sdk', check: 'sdk' },
  { name: 'subscribeChannel', epic: 'SDK', priority: 'P2', qa: 'auto-sdk', check: 'sdk' },
  { name: 'subscribeToAuthChanges', epic: 'SDK', priority: 'P2', qa: 'auto-sdk', check: 'sdk' },
  { name: 'subscribeToPlan', epic: 'SDK', priority: 'P2', qa: 'auto-sdk', check: 'sdk' },
  { name: 'updateFeatureFlag', epic: 'SDK', priority: 'P2', qa: 'auto-sdk', check: 'sdk' },
  { name: 'updateMemberRole', epic: 'SDK', priority: 'P2', qa: 'auto-sdk', check: 'sdk' },
  { name: 'updatePlanDetails', epic: 'SDK', priority: 'P2', qa: 'auto-sdk', check: 'sdk' },
  { name: 'updatePlanStatus', epic: 'SDK', priority: 'P2', qa: 'auto-sdk', check: 'sdk' },
  { name: 'updateProjectComponents', epic: 'SDK', priority: 'P2', qa: 'auto-sdk', check: 'sdk' },
  { name: 'updateProjectPackage', epic: 'SDK', priority: 'P2', qa: 'auto-sdk', check: 'sdk' },
  { name: 'updateProjectSettings', epic: 'SDK', priority: 'P2', qa: 'auto-sdk', check: 'sdk' },
  { name: 'updateProjectTier', epic: 'SDK', priority: 'P2', qa: 'auto-sdk', check: 'sdk' },
  { name: 'upgradeSubscription', epic: 'SDK', priority: 'P2', qa: 'auto-sdk', check: 'sdk' },

  // ═══ auto-props (DITD) ═══
  // DOMQL core
  { name: 'extends', epic: 'No-code Props', priority: 'P0', qa: 'auto-props', check: 'ditd', category: 'DOMQL' },
  { name: 'tag', epic: 'No-code Props', priority: 'P0', qa: 'auto-props', check: 'ditd', category: 'DOMQL' },
  { name: 'text', epic: 'No-code Props', priority: 'P0', qa: 'auto-props', check: 'ditd', category: 'DOMQL' },
  { name: 'html', epic: 'No-code Props', priority: 'P0', qa: 'auto-props', check: 'ditd', category: 'DOMQL' },
  { name: 'class', epic: 'No-code Props', priority: 'P0', qa: 'auto-props', check: 'ditd', category: 'DOMQL' },
  { name: 'state', epic: 'No-code Props', priority: 'P0', qa: 'auto-props', check: 'ditd', category: 'DOMQL' },
  { name: 'data', epic: 'No-code Props', priority: 'P0', qa: 'auto-props', check: 'ditd', category: 'DOMQL' },
  { name: 'scope', epic: 'No-code Props', priority: 'P0', qa: 'auto-props', check: 'ditd', category: 'DOMQL' },
  { name: 'children', epic: 'No-code Props', priority: 'P0', qa: 'auto-props', check: 'ditd', category: 'DOMQL' },
  { name: 'childrenAs', epic: 'No-code Props', priority: 'P0', qa: 'auto-props', check: 'ditd', category: 'DOMQL' },
  // Atoms
  { name: 'src', epic: 'No-code Props', priority: 'P0', qa: 'auto-props', check: 'ditd', category: 'Atoms' },
  { name: 'icon', epic: 'No-code Props', priority: 'P0', qa: 'auto-props', check: 'ditd', category: 'Icon' },
  { name: 'iconModifier', epic: 'No-code Props', priority: 'P0', qa: 'auto-props', check: 'ditd', category: 'Icon' },
  { name: 'shape', epic: 'No-code Props', priority: 'P0', qa: 'auto-props', check: 'ditd', category: 'Shape' },
  { name: 'shapeModifier', epic: 'No-code Props', priority: 'P0', qa: 'auto-props', check: 'ditd', category: 'Shape' },
  // Animation
  { name: 'animation', epic: 'No-code Props', priority: 'P0', qa: 'auto-props', check: 'ditd', category: 'Animation' },
  { name: 'transition', epic: 'No-code Props', priority: 'P0', qa: 'auto-props', check: 'ditd', category: 'Animation' },
  // Block
  { name: 'show', epic: 'No-code Props', priority: 'P0', qa: 'auto-props', check: 'ditd', category: 'Block' },
  { name: 'hide', epic: 'No-code Props', priority: 'P0', qa: 'auto-props', check: 'ditd', category: 'Block' },
  { name: 'height', epic: 'No-code Props', priority: 'P0', qa: 'auto-props', check: 'ditd', category: 'Block' },
  { name: 'width', epic: 'No-code Props', priority: 'P0', qa: 'auto-props', check: 'ditd', category: 'Block' },
  { name: 'size', epic: 'No-code Props', priority: 'P0', qa: 'auto-props', check: 'ditd', category: 'Block' },
  { name: 'boxSizing', epic: 'No-code Props', priority: 'P0', qa: 'auto-props', check: 'ditd', category: 'Block' },
  { name: 'padding', epic: 'No-code Props', priority: 'P0', qa: 'auto-props', check: 'ditd', category: 'Block' },
  { name: 'margin', epic: 'No-code Props', priority: 'P0', qa: 'auto-props', check: 'ditd', category: 'Block' },
  { name: 'gap', epic: 'No-code Props', priority: 'P0', qa: 'auto-props', check: 'ditd', category: 'Block' },
  { name: 'round', epic: 'No-code Props', priority: 'P0', qa: 'auto-props', check: 'ditd', category: 'Block' },
  { name: 'borderWidth', epic: 'No-code Props', priority: 'P0', qa: 'auto-props', check: 'ditd', category: 'Block' },
  // Flex
  { name: 'flow', epic: 'No-code Props', priority: 'P0', qa: 'auto-props', check: 'ditd', category: 'Flex' },
  { name: 'wrap', epic: 'No-code Props', priority: 'P0', qa: 'auto-props', check: 'ditd', category: 'Flex' },
  { name: 'align', epic: 'No-code Props', priority: 'P0', qa: 'auto-props', check: 'ditd', category: 'Flex' },
  // Font
  { name: 'fontSize', epic: 'No-code Props', priority: 'P0', qa: 'auto-props', check: 'ditd', category: 'Font' },
  { name: 'fontFamily', epic: 'No-code Props', priority: 'P0', qa: 'auto-props', check: 'ditd', category: 'Font' },
  { name: 'fontWeight', epic: 'No-code Props', priority: 'P0', qa: 'auto-props', check: 'ditd', category: 'Font' },
  // Grid
  { name: 'area', epic: 'No-code Props', priority: 'P0', qa: 'auto-props', check: 'ditd', category: 'Grid' },
  { name: 'template', epic: 'No-code Props', priority: 'P0', qa: 'auto-props', check: 'ditd', category: 'Grid' },
  { name: 'columns', epic: 'No-code Props', priority: 'P0', qa: 'auto-props', check: 'ditd', category: 'Grid' },
  { name: 'rows', epic: 'No-code Props', priority: 'P0', qa: 'auto-props', check: 'ditd', category: 'Grid' },
  // Position
  { name: 'inset', epic: 'No-code Props', priority: 'P0', qa: 'auto-props', check: 'ditd', category: 'Position' },
  { name: 'left', epic: 'No-code Props', priority: 'P0', qa: 'auto-props', check: 'ditd', category: 'Position' },
  { name: 'top', epic: 'No-code Props', priority: 'P0', qa: 'auto-props', check: 'ditd', category: 'Position' },
  { name: 'right', epic: 'No-code Props', priority: 'P0', qa: 'auto-props', check: 'ditd', category: 'Position' },
  { name: 'bottom', epic: 'No-code Props', priority: 'P0', qa: 'auto-props', check: 'ditd', category: 'Position' },
  // Theme
  { name: 'theme', epic: 'No-code Props', priority: 'P0', qa: 'auto-props', check: 'ditd', category: 'Theme' },
  { name: 'color', epic: 'No-code Props', priority: 'P0', qa: 'auto-props', check: 'ditd', category: 'Theme' },
  { name: 'background', epic: 'No-code Props', priority: 'P0', qa: 'auto-props', check: 'ditd', category: 'Theme' },
  { name: 'border', epic: 'No-code Props', priority: 'P0', qa: 'auto-props', check: 'ditd', category: 'Theme' },
  { name: 'borderColor', epic: 'No-code Props', priority: 'P0', qa: 'auto-props', check: 'ditd', category: 'Theme' },
  { name: 'shadow', epic: 'No-code Props', priority: 'P0', qa: 'auto-props', check: 'ditd', category: 'Theme' },
  { name: 'outline', epic: 'No-code Props', priority: 'P0', qa: 'auto-props', check: 'ditd', category: 'Theme' }
]

// ── Persistent state ──
let _featureStatus = {}
// ticketId linked to a feature+col — tracks which ticket is assigned
let _linkedTickets = {}

const _key = (feature, col, sub) => sub ? `${feature}:${col}:${sub}` : `${feature}:${col}`
const _getStatus = (feature, col, sub) => _featureStatus[_key(feature, col, sub)] || 'unchecked'
const _setStatus = (feature, col, status, sub) => { _featureStatus[_key(feature, col, sub)] = status }
const _getTicket = (feature, col, sub) => _linkedTickets[_key(feature, col, sub)] || null
const _setTicket = (feature, col, ticketId, sub) => { _linkedTickets[_key(feature, col, sub)] = ticketId }

const _loadJson = (key) => { try { const v = localStorage.getItem(key); return v ? JSON.parse(v) : null } catch (e) { return null } }
_featureStatus = _loadJson('featuresMatrix') || {}
_linkedTickets = _loadJson('featuresTickets') || {}

const _persist = () => {
  try {
    localStorage.setItem('featuresMatrix', JSON.stringify(_featureStatus))
    localStorage.setItem('featuresTickets', JSON.stringify(_linkedTickets))
  } catch (e) { /* ignore */ }
}

// ── Tooltip ──
let _tooltip = null

const _ensureTooltip = () => {
  if (_tooltip) return _tooltip
  _tooltip = document.createElement('div')
  _tooltip.style.cssText = `
    position:fixed;z-index:9999;pointer-events:none;
    padding:8px 12px;border-radius:6px;
    background:var(--color-overlayStrong, #1a1a1e);
    border:1px solid var(--color-line, #333);
    box-shadow:0 8px 24px -4px rgba(0,0,0,0.5);
    opacity:0;transition:opacity 120ms ease;
    max-width:320px;font-size:12px;line-height:1.4;
  `
  document.body.appendChild(_tooltip)
  return _tooltip
}

const _showTooltip = (anchor, content) => {
  const tip = _ensureTooltip()
  tip.innerHTML = content
  tip.style.opacity = '1'
  const rect = anchor.getBoundingClientRect()
  const tipW = tip.offsetWidth
  let x = rect.left + rect.width / 2 - tipW / 2
  let y = rect.bottom + 6
  if (x < 8) x = 8
  if (x + tipW > window.innerWidth - 8) x = window.innerWidth - tipW - 8
  if (y + tip.offsetHeight > window.innerHeight - 8) y = rect.top - tip.offsetHeight - 6
  tip.style.left = x + 'px'
  tip.style.top = y + 'px'
}

const _hideTooltip = () => { if (_tooltip) _tooltip.style.opacity = '0' }

const _nextStatus = (current) => {
  if (current === 'unchecked') return 'pass'
  if (current === 'pass') return 'fail'
  return 'unchecked'
}

// ── Ticket creation popup ──
let _popup = null

const _removePopup = () => {
  if (_popup && _popup.parentNode) _popup.parentNode.removeChild(_popup)
  _popup = null
}

const _positionPopup = (popup, anchor) => {
  const rect = anchor.getBoundingClientRect()
  const pw = 300; const ph = popup.offsetHeight || 280

  // Try below-right first
  let x = rect.left + rect.width / 2 - pw / 2
  let y = rect.bottom + 8

  // Clamp horizontal
  if (x < 12) x = 12
  if (x + pw > window.innerWidth - 12) x = window.innerWidth - pw - 12

  // If overflows bottom, show above
  if (y + ph > window.innerHeight - 12) y = rect.top - ph - 8
  // If still overflows top, center vertically
  if (y < 12) y = Math.max(12, (window.innerHeight - ph) / 2)

  popup.style.left = x + 'px'
  popup.style.top = y + 'px'
}

const _showTicketPopup = (anchor, feat, col, sub, s, rebuild) => {
  _removePopup()
  const linked = _getTicket(feat.name, col, sub)
  const label = col + (sub ? ' / ' + sub : '')

  // Backdrop
  const backdrop = document.createElement('div')
  backdrop.style.cssText = 'position:fixed;inset:0;z-index:9999;background:rgba(0,0,0,0.3);'
  document.body.appendChild(backdrop)

  _popup = document.createElement('div')
  _popup.style.cssText = `
    position:fixed;z-index:10000;
    padding:14px;border-radius:10px;width:300px;
    background:var(--color-overlayStrong, #1a1a1e);
    border:1px solid var(--color-line, #333);
    box-shadow:0 16px 48px -8px rgba(0,0,0,0.7);
    font-size:12px;color:var(--color-title);
  `
  document.body.appendChild(_popup)

  const esc = (str) => String(str).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;')

  _popup.innerHTML = `
    <div style="display:flex;align-items:center;justify-content:space-between;margin-bottom:10px;">
      <div style="font-weight:700;color:var(--color-red);font-size:13px;">Bug Report</div>
      <button id="fp-close" style="background:none;border:none;color:var(--color-caption);cursor:pointer;font-size:16px;padding:0 2px;">✕</button>
    </div>
    <div style="color:var(--color-title);font-weight:600;margin-bottom:4px;">${esc(feat.name)}</div>
    <div style="color:var(--color-caption);margin-bottom:12px;font-size:11px;">${label} · ${feat.epic} · ${feat.priority}</div>

    <div style="display:flex;gap:4px;margin-bottom:12px;">
      <button id="fp-tab-new" class="fp-tab" style="flex:1;padding:6px;border-radius:6px;border:1px solid var(--color-red);background:var(--color-red);color:var(--color-white);font-size:11px;font-weight:700;cursor:pointer;">New ticket</button>
      <button id="fp-tab-assign" class="fp-tab" style="flex:1;padding:6px;border-radius:6px;border:1px solid var(--color-line);background:var(--color-overlay);color:var(--color-caption);font-size:11px;font-weight:600;cursor:pointer;">Assign existing</button>
    </div>

    <div id="fp-panel-new">
      <div style="display:flex;gap:4px;margin-bottom:8px;">
        <button class="fp-type" data-type="Bug" style="flex:1;padding:5px;border-radius:4px;border:2px solid var(--color-red);background:var(--color-red);color:var(--color-white);font-size:10px;font-weight:700;cursor:pointer;">Bug</button>
        <button class="fp-type" data-type="Feature" style="flex:1;padding:5px;border-radius:4px;border:2px solid var(--color-line);background:var(--color-overlay);color:var(--color-caption);font-size:10px;font-weight:700;cursor:pointer;">Feature</button>
        <button class="fp-type" data-type="Enhancement" style="flex:1;padding:5px;border-radius:4px;border:2px solid var(--color-line);background:var(--color-overlay);color:var(--color-caption);font-size:10px;font-weight:700;cursor:pointer;">Enhancement</button>
        <button class="fp-type" data-type="Task" style="flex:1;padding:5px;border-radius:4px;border:2px solid var(--color-line);background:var(--color-overlay);color:var(--color-caption);font-size:10px;font-weight:700;cursor:pointer;">Task</button>
      </div>
      <input id="fp-title" type="text" value="${esc(`[QA] ${feat.name} — ${label}`)}" style="width:100%;box-sizing:border-box;padding:6px 8px;border:1px solid var(--color-line);border-radius:4px;background:var(--color-overlay);color:var(--color-title);font-size:11px;margin-bottom:6px;" />
      <textarea id="fp-desc" rows="3" style="width:100%;box-sizing:border-box;padding:6px 8px;border:1px solid var(--color-line);border-radius:4px;background:var(--color-overlay);color:var(--color-title);font-size:11px;margin-bottom:8px;resize:vertical;">${feat.name} (${feat.epic}, ${feat.priority}) failing on ${label}.\nReported from Features Matrix.</textarea>
      <button id="fp-create" style="width:100%;padding:8px;border:none;border-radius:6px;background:var(--color-red);color:var(--color-white);font-size:12px;font-weight:700;cursor:pointer;">Create ticket</button>
    </div>

    <div id="fp-panel-assign" style="display:none;">
      <input id="fp-ticket-id" type="text" placeholder="Enter ticket ID or title..." value="${linked || ''}" style="width:100%;box-sizing:border-box;padding:6px 8px;border:1px solid var(--color-line);border-radius:4px;background:var(--color-overlay);color:var(--color-title);font-size:11px;margin-bottom:8px;" />
      <button id="fp-assign" style="width:100%;padding:8px;border:none;border-radius:6px;background:var(--color-blue);color:var(--color-white);font-size:12px;font-weight:700;cursor:pointer;">Link ticket</button>
      ${linked ? `<button id="fp-unlink" style="width:100%;padding:6px;border:none;border-radius:4px;background:none;color:var(--color-caption);font-size:11px;cursor:pointer;margin-top:4px;">Unlink current ticket</button>` : ''}
    </div>
  `

  _positionPopup(_popup, anchor)

  const close = () => {
    _removePopup()
    if (backdrop.parentNode) backdrop.parentNode.removeChild(backdrop)
  }

  backdrop.onclick = close
  _popup.querySelector('#fp-close').onclick = close

  // Tab switching
  const tabNew = _popup.querySelector('#fp-tab-new')
  const tabAssign = _popup.querySelector('#fp-tab-assign')
  const panelNew = _popup.querySelector('#fp-panel-new')
  const panelAssign = _popup.querySelector('#fp-panel-assign')

  tabNew.onclick = () => {
    tabNew.style.background = 'var(--color-red)'; tabNew.style.color = 'var(--color-white)'; tabNew.style.borderColor = 'var(--color-red)'
    tabAssign.style.background = 'var(--color-overlay)'; tabAssign.style.color = 'var(--color-caption)'; tabAssign.style.borderColor = 'var(--color-line)'
    panelNew.style.display = ''; panelAssign.style.display = 'none'
  }
  tabAssign.onclick = () => {
    tabAssign.style.background = 'var(--color-blue)'; tabAssign.style.color = 'var(--color-white)'; tabAssign.style.borderColor = 'var(--color-blue)'
    tabNew.style.background = 'var(--color-overlay)'; tabNew.style.color = 'var(--color-caption)'; tabNew.style.borderColor = 'var(--color-line)'
    panelNew.style.display = 'none'; panelAssign.style.display = ''
  }

  // If already linked, show assign tab
  if (linked) tabAssign.onclick()

  // Type selector
  const TYPE_COLORS = { Bug: 'red', Feature: 'green', Enhancement: 'green', Task: 'blue' }
  let selectedType = 'Bug'
  const typeButtons = _popup.querySelectorAll('.fp-type')
  const updateTypeButtons = () => {
    typeButtons.forEach(btn => {
      const t = btn.dataset.type
      const active = t === selectedType
      const c = TYPE_COLORS[t] || 'caption'
      btn.style.background = active ? `var(--color-${c})` : 'var(--color-overlay)'
      btn.style.color = active ? 'var(--color-white)' : 'var(--color-caption)'
      btn.style.borderColor = active ? `var(--color-${c})` : 'var(--color-line)'
    })
  }
  typeButtons.forEach(btn => {
    btn.onclick = () => { selectedType = btn.dataset.type; updateTypeButtons() }
  })

  // Create ticket — opens the real TicketDetailModal
  _popup.querySelector('#fp-create').onclick = () => {
    const title = _popup.querySelector('#fp-title').value
    const desc = _popup.querySelector('#fp-desc').value
    const epicLabel = feat.epic.toLowerCase().replace(/\s+/g, '-')
    const root = s.root
    if (root) {
      root.update({
        selectedTicket: { _new: true },
        ticketEditMode: true,
        ticketDraft: {
          title,
          body: desc,
          columnKey: 'backlog',
          labels: [selectedType, epicLabel, 'QA'].filter(Boolean),
          priority: feat.priority === 'P0' ? 'urgent' : feat.priority === 'P1' ? 'high' : 'medium',
          dueDate: '',
          assigneeEmail: ''
        }
      })
    }
    close()
  }

  // Assign existing ticket
  _popup.querySelector('#fp-assign').onclick = () => {
    const ticketId = _popup.querySelector('#fp-ticket-id').value.trim()
    if (ticketId) {
      _setTicket(feat.name, col, ticketId, sub)
      _persist()
      rebuild()
    }
    close()
  }

  // Unlink
  const unlinkBtn = _popup.querySelector('#fp-unlink')
  if (unlinkBtn) {
    unlinkBtn.onclick = () => {
      _setTicket(feat.name, col, null, sub)
      _persist()
      rebuild()
      close()
    }
  }
}

// ── Filter state ──
let _activeEpic = null
let _activePriority = null
let _statsEl = null
let _collapsedGroups = _loadJson('featuresCollapsed') || {}

const _rebuildStats = () => { if (_statsEl) _buildStats(_statsEl) }

// ── Cell builder ──

const _makeCell = (el, s, feat, col, sub, rebuild) => {
  const status = _getStatus(feat.name, col, sub)
  const st = STATUS[status] || STATUS.unchecked
  const ticket = _getTicket(feat.name, col, sub)

  const td = document.createElement('div')
  td.style.cssText = 'padding:4px 2px;border-bottom:1px solid var(--color-line);display:flex;align-items:center;justify-content:center;cursor:pointer;position:relative;'

  const dot = document.createElement('span')
  const sz = sub ? '12px' : '16px'
  dot.style.cssText = `width:${sz};height:${sz};border-radius:3px;background:var(--color-${st.color});transition:transform 100ms,opacity 100ms;opacity:0.85;`
  td.appendChild(dot)

  // Show ticket indicator if linked
  if (ticket && status === 'fail') {
    const badge = document.createElement('span')
    badge.style.cssText = 'position:absolute;top:1px;right:1px;width:5px;height:5px;border-radius:50%;background:var(--color-blue);'
    td.appendChild(badge)
  }

  td.onmouseenter = () => {
    dot.style.opacity = '1'
    dot.style.transform = 'scale(1.2)'
    let html = `<div style="font-weight:700;color:var(--color-title);">${feat.name}</div>`
    html += `<div style="color:var(--color-caption);font-size:11px;margin:2px 0;">${col}${sub ? ' / ' + sub : ''}</div>`
    html += `<div style="color:var(--color-${st.color});font-weight:600;">${st.label}</div>`
    if (ticket) html += `<div style="color:var(--color-blue);font-size:10px;margin-top:2px;">Ticket: ${String(ticket).replace(/</g, '&lt;')}</div>`
    html += `<div style="color:var(--color-caption);font-size:10px;margin-top:4px;">Click to cycle • Right-click for ticket</div>`
    _showTooltip(td, html)
  }
  td.onmouseleave = () => {
    dot.style.opacity = '0.85'
    dot.style.transform = 'scale(1)'
    _hideTooltip()
  }

  td.onclick = (e) => {
    e.preventDefault()
    e.stopPropagation()
    const current = _getStatus(feat.name, col, sub)
    const next = _nextStatus(current)
    _setStatus(feat.name, col, next, sub)
    // When going to fail, show ticket popup
    if (next === 'fail') {
      _persist()
      rebuild()
      _rebuildStats()
      _showTicketPopup(td, feat, col, sub, s, rebuild)
      return
    }
    _persist()
    rebuild()
    _rebuildStats()
  }

  td.oncontextmenu = (e) => {
    e.preventDefault()
    e.stopPropagation()
    // Always show ticket popup on right-click
    if (_getStatus(feat.name, col, sub) !== 'fail') {
      _setStatus(feat.name, col, 'fail', sub)
      _persist()
      rebuild()
      _rebuildStats()
    }
    _showTicketPopup(td, feat, col, sub, s, rebuild)
  }

  return td
}

// ── Label cell ──

const _makeLabel = (feat) => {
  const label = document.createElement('div')
  label.style.cssText = 'padding:6px 8px;border-bottom:1px solid var(--color-line);font-size:12px;font-weight:500;color:var(--color-title);display:flex;align-items:center;gap:6px;white-space:nowrap;overflow:hidden;'

  const prioTag = document.createElement('span')
  prioTag.style.cssText = `font-size:9px;font-weight:700;padding:1px 4px;border-radius:3px;flex-shrink:0;color:var(--color-white);background:var(--color-${feat.priority === 'P0' ? 'red' : feat.priority === 'P1' ? 'orange' : 'caption'});`
  prioTag.textContent = feat.priority
  label.appendChild(prioTag)

  const nameSpan = document.createElement('span')
  nameSpan.style.cssText = 'overflow:hidden;text-overflow:ellipsis;'
  nameSpan.textContent = feat.name
  if (feat.category) {
    const catSpan = document.createElement('span')
    catSpan.style.cssText = 'color:var(--color-caption);font-size:10px;font-weight:400;margin-left:4px;'
    catSpan.textContent = feat.category
    nameSpan.appendChild(catSpan)
  }
  label.appendChild(nameSpan)

  // Show epic tag for mixed groups
  if (feat.epic && feat.qa !== 'manual-sidebar' && feat.qa !== 'auto-props') {
    const epicTag = document.createElement('span')
    epicTag.style.cssText = 'font-size:9px;padding:1px 4px;border-radius:3px;background:var(--color-line);color:var(--color-caption);flex-shrink:0;margin-left:auto;'
    epicTag.textContent = feat.epic
    label.appendChild(epicTag)
  }

  return label
}

// ── Grid builder ──

const _buildMatrix = (el, s) => {
  if (!el.node) return
  el.node.innerHTML = ''

  const wrapper = document.createElement('div')
  wrapper.style.cssText = 'display:flex;flex-direction:column;gap:16px;'
  const rebuild = () => _buildMatrix(el, s)

  for (const group of QA_GROUPS) {
    const groupFeatures = FEATURES.filter(f => {
      if (f.qa !== group.key) return false
      if (_activeEpic && f.epic !== _activeEpic) return false
      if (_activePriority && f.priority !== _activePriority) return false
      return true
    })
    if (!groupFeatures.length) continue

    const isCollapsed = _collapsedGroups[group.key]

    // Group header
    const header = document.createElement('div')
    header.style.cssText = 'display:flex;align-items:center;gap:10px;cursor:pointer;padding:12px 16px;border-radius:10px;background:var(--color-overlay);border:1px solid var(--color-line);user-select:none;'
    header.onclick = () => {
      _collapsedGroups[group.key] = !_collapsedGroups[group.key]
      try { localStorage.setItem('featuresCollapsed', JSON.stringify(_collapsedGroups)) } catch (e) { /* ignore */ }
      rebuild()
    }

    const arrow = document.createElement('span')
    arrow.style.cssText = `font-size:10px;color:var(--color-caption);transition:transform 150ms;transform:rotate(${isCollapsed ? '0' : '90'}deg);`
    arrow.textContent = '▶'
    header.appendChild(arrow)

    const isManual = group.key.startsWith('manual')
    const badge = document.createElement('span')
    badge.style.cssText = `font-size:9px;font-weight:700;padding:2px 6px;border-radius:4px;letter-spacing:0.04em;text-transform:uppercase;background:var(--color-${isManual ? 'orange' : 'blue'});color:var(--color-white);`
    badge.textContent = isManual ? 'Manual' : 'Auto'
    header.appendChild(badge)

    const titleEl = document.createElement('div')
    titleEl.style.cssText = 'flex:1;'
    titleEl.innerHTML = `<span style="font-weight:700;font-size:14px;color:var(--color-title);">${group.title}</span><br><span style="font-size:11px;color:var(--color-caption);">${group.subtitle}</span>`
    header.appendChild(titleEl)

    const { total: gTotal, pass: gPass, fail: gFail } = _countStatuses(groupFeatures)

    const statsEl = document.createElement('div')
    statsEl.style.cssText = 'display:flex;align-items:center;gap:8px;flex-shrink:0;'
    const gPct = gTotal > 0 ? Math.round((gPass / gTotal) * 100) : 0
    statsEl.innerHTML = `
      <span style="font-size:11px;color:var(--color-green);font-weight:700;">${gPass}</span>
      ${gFail ? `<span style="font-size:11px;color:var(--color-red);font-weight:700;">${gFail}</span>` : ''}
      <span style="font-size:10px;color:var(--color-caption);">/ ${gTotal}</span>
      <div style="width:60px;height:6px;border-radius:3px;background:var(--color-line);overflow:hidden;">
        <div style="height:100%;border-radius:3px;background:var(--color-green);width:${gPct}%;"></div>
      </div>
      <span style="font-size:11px;font-weight:700;color:var(--color-${gPct > 80 ? 'green' : gPct > 40 ? 'orange' : 'caption'});">${gPct}%</span>
    `
    header.appendChild(statsEl)
    wrapper.appendChild(header)

    if (isCollapsed) continue

    // Group by epic within the QA group
    const grouped = {}
    for (const f of groupFeatures) {
      const gk = f.epic
      if (!grouped[gk]) grouped[gk] = []
      grouped[gk].push(f)
    }

    for (const [epic, features] of Object.entries(grouped)) {
      const section = document.createElement('div')
      section.style.cssText = 'padding:12px 16px;border-radius:10px;background:var(--color-overlay);border:1px solid var(--color-line);overflow-x:auto;margin-left:24px;'

      const h4 = document.createElement('h4')
      h4.style.cssText = 'margin:0 0 10px 0;font-size:13px;font-weight:700;color:var(--color-title);'
      h4.textContent = epic
      section.appendChild(h4)

      const checkType = features[0]?.check || 'env'

      if (checkType === 'crud') {
        const subs = CHECK_TYPES.crud.subs
        const totalCols = ENVS.length * subs.length
        const grid = document.createElement('div')
        grid.style.cssText = `display:grid;grid-template-columns:minmax(150px,1fr) repeat(${totalCols},28px);gap:0;min-width:fit-content;`

        const corner = document.createElement('div')
        corner.style.cssText = 'padding:4px 8px;border-bottom:1px solid var(--color-line);font-size:10px;font-weight:700;letter-spacing:.06em;text-transform:uppercase;color:var(--color-caption);grid-row:1/3;display:flex;align-items:end;'
        corner.textContent = 'Feature'
        grid.appendChild(corner)

        for (const env of ENVS) {
          const th = document.createElement('div')
          th.style.cssText = `padding:3px 2px;border-bottom:1px solid var(--color-line);font-size:9px;font-weight:700;letter-spacing:.04em;text-transform:uppercase;color:var(--color-caption);text-align:center;grid-column:span ${subs.length};`
          th.textContent = env
          grid.appendChild(th)
        }
        for (const env of ENVS) {
          for (const sub of subs) {
            const th = document.createElement('div')
            th.style.cssText = 'padding:2px;border-bottom:2px solid var(--color-line);font-size:8px;font-weight:700;color:var(--color-caption);text-align:center;'
            th.textContent = sub
            grid.appendChild(th)
          }
        }
        for (const feat of features) {
          grid.appendChild(_makeLabel(feat))
          for (const env of ENVS) for (const sub of subs) grid.appendChild(_makeCell(el, s, feat, env, sub, rebuild))
        }
        section.appendChild(grid)
      } else if (checkType === 'ditd' || checkType === 'sdk') {
        const cols = CHECK_TYPES[checkType].columns
        const grid = document.createElement('div')
        grid.style.cssText = `display:grid;grid-template-columns:minmax(180px,1fr) repeat(${cols.length},72px);gap:0;`

        const corner = document.createElement('div')
        corner.style.cssText = 'padding:4px 8px;border-bottom:2px solid var(--color-line);font-size:10px;font-weight:700;letter-spacing:.06em;text-transform:uppercase;color:var(--color-caption);'
        corner.textContent = checkType === 'sdk' ? 'Function' : 'Prop'
        grid.appendChild(corner)
        for (const col of cols) {
          const th = document.createElement('div')
          th.style.cssText = 'padding:4px;border-bottom:2px solid var(--color-line);font-size:9px;font-weight:700;letter-spacing:.04em;text-transform:uppercase;color:var(--color-caption);text-align:center;'
          th.textContent = col.length > 6 ? col.slice(0, 5) : col
          grid.appendChild(th)
        }
        for (const feat of features) {
          grid.appendChild(_makeLabel(feat))
          for (const col of cols) grid.appendChild(_makeCell(el, s, feat, col, null, rebuild))
        }
        section.appendChild(grid)
      } else {
        const grid = document.createElement('div')
        grid.style.cssText = `display:grid;grid-template-columns:minmax(180px,1fr) repeat(${ENVS.length},72px);gap:0;`

        const corner = document.createElement('div')
        corner.style.cssText = 'padding:4px 8px;border-bottom:2px solid var(--color-line);font-size:10px;font-weight:700;letter-spacing:.06em;text-transform:uppercase;color:var(--color-caption);'
        corner.textContent = 'Feature'
        grid.appendChild(corner)
        for (const env of ENVS) {
          const th = document.createElement('div')
          th.style.cssText = 'padding:4px;border-bottom:2px solid var(--color-line);font-size:10px;font-weight:700;letter-spacing:.06em;text-transform:uppercase;color:var(--color-caption);text-align:center;'
          th.textContent = env
          grid.appendChild(th)
        }
        for (const feat of features) {
          grid.appendChild(_makeLabel(feat))
          for (const env of ENVS) grid.appendChild(_makeCell(el, s, feat, env, null, rebuild))
        }
        section.appendChild(grid)
      }

      wrapper.appendChild(section)
    }
  }

  el.node.appendChild(wrapper)
}

// ── Filters ──

const _allEpics = [...new Set(FEATURES.map(f => f.epic))]

const _buildFilters = (el, s, matrixEl) => {
  if (!el.node) return
  el.node.innerHTML = ''

  const bar = document.createElement('div')
  bar.style.cssText = 'display:flex;gap:6px;flex-wrap:wrap;align-items:center;'

  const mkBtn = (label, active, onClick) => {
    const btn = document.createElement('button')
    btn.style.cssText = `padding:4px 10px;border-radius:6px;border:1px solid var(--color-line);font-size:11px;font-weight:600;cursor:pointer;background:${active ? 'var(--color-title)' : 'var(--color-overlay)'};color:${active ? 'var(--color-background)' : 'var(--color-caption)'};transition:all 100ms;`
    btn.textContent = label
    btn.onclick = onClick
    return btn
  }

  bar.appendChild(mkBtn('All', !_activeEpic, () => { _activeEpic = null; _buildFilters(el, s, matrixEl); _buildMatrix(matrixEl, s) }))

  for (const epic of _allEpics) {
    bar.appendChild(mkBtn(epic, _activeEpic === epic, () => {
      _activeEpic = _activeEpic === epic ? null : epic
      _buildFilters(el, s, matrixEl); _buildMatrix(matrixEl, s)
    }))
  }

  const sep = document.createElement('span')
  sep.style.cssText = 'width:1px;height:20px;background:var(--color-line);margin:0 4px;'
  bar.appendChild(sep)

  for (const p of ['P0', 'P1', 'P2']) {
    const active = _activePriority === p
    const pColor = p === 'P0' ? 'red' : p === 'P1' ? 'orange' : 'caption'
    const btn = document.createElement('button')
    btn.style.cssText = `padding:4px 8px;border-radius:6px;border:1px solid var(--color-${active ? pColor : 'line'});font-size:11px;font-weight:700;cursor:pointer;background:${active ? `var(--color-${pColor})` : 'var(--color-overlay)'};color:${active ? 'var(--color-white)' : `var(--color-${pColor})`};transition:all 100ms;`
    btn.textContent = p
    btn.onclick = () => { _activePriority = active ? null : p; _buildFilters(el, s, matrixEl); _buildMatrix(matrixEl, s) }
    bar.appendChild(btn)
  }

  el.node.appendChild(bar)
}

// ── Stats ──

const _countStatuses = (features) => {
  let total = 0; let pass = 0; let fail = 0; let unchecked = 0
  for (const f of features) {
    const ct = f.check || 'env'
    if (ct === 'crud') {
      for (const env of ENVS) for (const sub of CHECK_TYPES.crud.subs) { total++; const st = _getStatus(f.name, env, sub); if (st === 'pass') pass++; else if (st === 'fail') fail++; else unchecked++ }
    } else if (ct === 'ditd' || ct === 'sdk') {
      for (const col of CHECK_TYPES[ct].columns) { total++; const st = _getStatus(f.name, col); if (st === 'pass') pass++; else if (st === 'fail') fail++; else unchecked++ }
    } else {
      for (const env of ENVS) { total++; const st = _getStatus(f.name, env); if (st === 'pass') pass++; else if (st === 'fail') fail++; else unchecked++ }
    }
  }
  return { total, pass, fail, unchecked }
}

const _buildStats = (el) => {
  if (!el.node) return
  el.node.innerHTML = ''

  const { total, pass, fail, unchecked } = _countStatuses(FEATURES)

  const bar = document.createElement('div')
  bar.style.cssText = 'display:flex;gap:20px;align-items:center;flex-wrap:wrap;'

  for (const item of [
    { label: 'Total', value: total, color: 'title' },
    { label: 'Passing', value: pass, color: 'green' },
    { label: 'Failing', value: fail, color: 'red' },
    { label: 'Unchecked', value: unchecked, color: 'gray' }
  ]) {
    const d = document.createElement('div')
    d.style.cssText = 'display:flex;align-items:baseline;gap:4px;'
    const val = document.createElement('span')
    val.style.cssText = `font-size:20px;font-weight:800;color:var(--color-${item.color});`
    val.textContent = item.value
    const lbl = document.createElement('span')
    lbl.style.cssText = 'font-size:11px;color:var(--color-caption);'
    lbl.textContent = item.label
    d.appendChild(val); d.appendChild(lbl)
    bar.appendChild(d)
  }

  const pct = total > 0 ? Math.round((pass / total) * 100) : 0
  const barWrap = document.createElement('div')
  barWrap.style.cssText = 'flex:1;max-width:200px;height:8px;border-radius:4px;background:var(--color-line);overflow:hidden;margin-left:8px;'
  const barFill = document.createElement('div')
  barFill.style.cssText = `height:100%;border-radius:4px;background:var(--color-green);width:${pct}%;transition:width 300ms;`
  barWrap.appendChild(barFill)
  bar.appendChild(barWrap)

  const pctLabel = document.createElement('span')
  pctLabel.style.cssText = 'font-size:12px;font-weight:700;color:var(--color-green);'
  pctLabel.textContent = `${pct}%`
  bar.appendChild(pctLabel)

  el.node.appendChild(bar)
}

const _matrixCard = {
  round: 'Z2', background: 'overlay', borderWidth: '1px', borderStyle: 'solid', borderColor: 'line', overflow: 'visible', minHeight: 'fit-content'
}

export const features = {
  extends: 'AppPage',
  metadata: { title: 'Features / Symbols Workspace' },
  fetch: appPageBoilerplateFetch,

  FeaturesMain: {
    extends: 'AppMain',
    padding: '0',

    Header: {
      extends: 'PageHeader',
      LeftGroup: { Title: { text: 'Features' } },
      RightGroup: {
        ResetBtn: {
          tag: 'button', type: 'button', text: 'Reset all',
          padding: 'Z A', round: 'Z',
          borderWidth: '1px', borderColor: 'line', borderStyle: 'solid',
          background: 'overlay', cursor: 'pointer', fontSize: 'Z2', fontWeight: '500', color: 'caption',
          ':hover': { background: 'overlayStrong', color: 'red' },
          onClick: (e, el, s) => {
            _featureStatus = {}; _linkedTickets = {}; _persist()
            const body = el.parent?.parent?.Body
            if (body) { if (body.MatrixGrid) _buildMatrix(body.MatrixGrid, s); if (body.StatsBar) _buildStats(body.StatsBar) }
          }
        }
      }
    },

    Body: {
      overflow: 'auto', padding: 'B', flow: 'y', gap: 'A',
      maxWidth: '1400px', margin: '0 auto', width: '100%',

      Hero: {
        flow: 'y', gap: 'Z', padding: '0 Z',
        Eyebrow: { tag: 'span', fontSize: 'Z2', fontWeight: '700', letterSpacing: '0.12em', textTransform: 'uppercase', color: 'blue', text: 'QA & Release tracking' },
        Title: { tag: 'h1', fontSize: 'E', fontWeight: '800', color: 'title', letterSpacing: '-0.03em', lineHeight: '1.1', margin: '0', text: 'Features Matrix' },
        Subtitle: { tag: 'p', fontSize: 'Z1', color: 'caption', margin: '0', text: 'Click to cycle status. When failing, a ticket prompt appears. Right-click to force ticket creation.' }
      },

      StatsBar: { ..._matrixCard, padding: 'A B', onRender: (el) => { _statsEl = el; _buildStats(el) } },

      Legend: {
        flow: 'x', gap: 'B', align: 'center left', padding: 'Z',
        childrenAs: 'state',
        children: () => Object.entries(STATUS).map(([key, v]) => ({ key, ...v })),
        childExtends: {
          flow: 'x', align: 'center left', gap: 'Y',
          Dot: { width: 'A', height: 'A', round: 'Y', background: (el, s) => s.color },
          Label: { tag: 'span', fontSize: 'Y2', color: 'caption', text: (el, s) => s.label }
        }
      },

      FilterBar: {
        padding: '0 Z',
        onRender: (el, s) => { requestAnimationFrame(() => { const m = el.parent?.MatrixGrid; if (m) _buildFilters(el, s, m) }) }
      },

      MatrixGrid: { onRender: (el, s) => _buildMatrix(el, s) },

      onRemove: () => {
        _removePopup()
        if (_tooltip && _tooltip.parentNode) _tooltip.parentNode.removeChild(_tooltip)
        _tooltip = null
        _statsEl = null
      }
    }
  }
}

export const ticketsStatus = {
  extends: 'AppPage',
  metadata: { title: 'Tickets status / Symbols Workspace' },
  fetch: pageBoilerplateFetch,
  TicketsSidebar: {},
  FeaturesMain: features.FeaturesMain,
  TicketsDetailModal: {}
}
